import { Link, useHistory } from "react-router-dom";
import React, { useState } from "react";
import { extractOnlyText, isMobileDevice, titleize } from "./helpers/utils";
import { useDispatch, useSelector } from "react-redux";

import FooterLinks from "./FooterLinks";
import OnboardingFlow from "./OnboardingFlow";
import Skeleton from "react-loading-skeleton";
import { actions } from "./redux/StateReducer";

const Dashboard = (props) => {
  let history = useHistory();
  const dispatch = useDispatch();

  const courseCurriculumOpen = useSelector(
    (state) => state.courseCurriculumOpen
  );
  const footerLinks = useSelector((state) => state.footerLinks);
  const user = useSelector((state) => state.user);
  const view = useSelector((state) => state.view);

  const bootstrap = useSelector((state) => state.dashboardSummary);
  const initialBootstrap = React.useRef(bootstrap);
  const [userInfo, setUserInfo] = useState(bootstrap || {});
  const [isLoaded, setIsLoaded] = useState(!!bootstrap);
  const [loadError, setLoadError] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState("");

  function updateSettings(e) {
    const previousLanguage = userInfo.language;
    const selectedLanguage = e.target.id === "languageSwitch" ? e.target.value : userInfo.language;
    setUserInfo(current => ({ ...current, language: selectedLanguage }));
    setSettingsStatus("Saving…");
    const token = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");

    fetch(`/api/settings`, {
      method: "put",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
      },
      body: JSON.stringify({
        brochure: {
          language: selectedLanguage,
          // huge hack, these should be separate functions
          view:
            e.target.checked !== undefined
              ? e.target.checked === true
                ? "interactive"
                : "article"
              : $("#viewSwitch").prop("checked")
              ? "interactive"
              : "article",
        },
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        return res.json();
      })
      .then(
        (obj) => {
          // hack

          setUserInfo(current => ({ ...current, language: obj.language }));
          setSettingsStatus("Saved");

          if (obj.view === "interactive") {
            $("#viewSwitch").prop("checked", true);
          } else {
            $("#viewSwitch").prop("checked", false);
          }

          dispatch({
            type: actions.SET_VIEW,
            payload: obj.view,
          });
          dispatch({
            type: actions.SET_APP_LANGUAGE,
            payload: obj.language,
          });
        },
        (error) => {
          setUserInfo(current => ({ ...current, language: previousLanguage }));
          setSettingsStatus("Could not save. Please try again.");
        }
      );
  }

  const fetchUserInfo = (signal) => {
    setLoadError(false);
    fetch(`/api/me?dashboard=1`, { signal })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to load dashboard");
        return res.json();
      })
      .then(
        (obj) => {
          setUserInfo(obj);
          setIsLoaded(true);

        },
        // Note: it's important to handle errors here
        // instead of a catch() block so that we don't swallow
        // exceptions from actual bugs in components.
        (error) => {
          if (error.name !== "AbortError") setLoadError(true);
        }
      );
  };

  React.useEffect(() => {
    if (!user) {
      return window.location.replace("/users/sign_in");
    }

    if (initialBootstrap.current) {
      initialBootstrap.current = null;
      dispatch({ type: "CLEAR_DASHBOARD_BOOTSTRAP" });
      return;
    }
    const controller = new AbortController();
    fetchUserInfo(controller.signal);
    return () => controller.abort();
  }, [user]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoaded]);

  if (!isLoaded) {
    if (loadError) return <div role="alert" className="p-4"><h2>We couldn’t load your dashboard.</h2><p>Your progress is saved. Please try again.</p><button className="ad-view-mode-button" onClick={() => fetchUserInfo()}>Try again</button></div>;
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          minHeight: "100vh",
        }}
      >
        <i className="fas fa-spinner fa-2x fa-pulse m-auto text-center"></i>
      </div>
    );
  }

  // kind of a hack - using first lesson completion as test for dashboard
  if (isLoaded && userInfo && !userInfo.onboarded) {
    return <OnboardingFlow fetchUserInfo={fetchUserInfo} userInfo={userInfo} />;
  }

  return (
    <>
      <div className={`${isMobileDevice() ? "p-2" : "p-0"} container ad-dashboard`}>
        <div className="small mb-2 ad-dashboard-breadcrumb">
          <strong>Today</strong> ·{" "}
          <Link to={`/activity-log`}>
            <strong>Activity Log</strong>
          </Link>{" "}
          ·{" "}
          <strong className="text-secondary">
            {(userInfo.points || 0).toString()}
          </strong>{" "}
          points
        </div>
        <h3
          className="mt-2 mb-4 ad-dashboard-title"
          style={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {userInfo ? (
            <>Make today count, {userInfo.name}.</>
          ) : (
            <Skeleton></Skeleton>
          )}
        </h3>
        <>
          <div className="row ad-dashboard-top-grid">
            <div className="col-lg-8 mb-4 rounded-lg">
              <div className="row no-gutters rounded-lg shadow ad-dashboard-next">
                <div
                  className="col-lg-6 bg-white d-none d-lg-flex align-items-center flex-column justify-content-center ad-dashboard-next-art"
                  style={{ minHeight: "12rem", maxHeight: "16rem" }}
                >
                  {userInfo.section_on && userInfo.section_on[0] ? <img
                    src={
                      userInfo &&
                      userInfo.section_on &&
                      userInfo.section_on[0].image_url
                    }
                    className="w-100"
                    style={{
                      objectFit: "cover",
                      height: "100%",
                    }}
                  /> : <div className="ad-dashboard-start"><span className="ad-kicker">Your next step</span><strong>Build a steady<br />practice habit.</strong></div>}
                </div>
                <div className="card col-lg-6 border-0 ad-dashboard-next-copy">
                  <div
                    className="card-body border-0 text-dark d-flex flex-column justify-content-center"
                    style={{
                      textShadow: "1px 1px #fff",
                    }}
                  >
                    {userInfo.section_on && userInfo.section_on.length ? (
                      <>
                        <p className="small mb-2 p-0">
                          TODAY'S FOCUS
                        </p>
                        <Link to={`/sections/${userInfo.section_on[0].slug}`}>
                          <h3 className="font-weight-bold mb-4">
                            {userInfo.section_on[0].title || titleize(
                              userInfo.section_on[0].slug.replace(/-[a-f0-9]{8}$/, "").replace(/-/g, " ")
                            )}
                          </h3>
                        </Link>
                      </>
                    ) : <><span className="ad-kicker">Start here</span><h3>Choose your first learning path.</h3><p>Pick a course and your next lesson will appear here.</p></>}
                    <button
                      className="ad-button ad-button-primary w-100"
                      onClick={() => {
                        if (!userInfo.next) {
                          return window.location.assign("/curriculum");
                        }

                        const tutorialType = userInfo.next.question
                          ? "challenges"
                          : "lessons";
                        const tutorialSlug =
                          userInfo.next && userInfo.next.slug;

                        const nextTutorial = `/${tutorialType}/${tutorialSlug}`;

                        history.push(nextTutorial);
                      }}
                    >
                      {userInfo.next ? "Continue learning" : "Choose a course"} <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 mb-4">
              <div className="card bg-white border-0 rounded-lg shadow h-100 ad-dashboard-preferences">
                <div className="card-body border-0 d-flex flex-column justify-content-center">
                  <span className="ad-kicker">Your setup</span>
                  <h3 className="font-weight-bold">Learning preferences</h3>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="viewSwitch"
                      disabled={settingsStatus === "Saving…"}
                      onChange={updateSettings}
                      checked={view === "interactive"}
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="viewSwitch"
                    >
                      Default to interactive view
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="customSwitch2"
                      checked={courseCurriculumOpen}
                      onChange={(e) => {
                        localStorage &&
                          localStorage.setItem(
                            "adCourseCurriculumOpen",
                            e.target.checked
                          );

                        dispatch({
                          type: actions.SET_COURSE_CURRICULUM_OPEN,
                          payload: e.target.checked,
                        });
                      }}
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="customSwitch2"
                    >
                      Keep course menu open
                    </label>
                  </div>
                  <select
                    className="custom-select custom-select-sm"
                    id="languageSwitch"
                    aria-label="Default programming language"
                    disabled={settingsStatus === "Saving…"}
                    value={userInfo.language || "javascript"}
                    onChange={updateSettings}
                  >
                    <option value="javascript">Default to Javascript</option>
                    <option value="python">Default to Python</option>
                    <option value="java">Default to Java</option>
                    <option value="go">Default to Go</option>
                    <option value="ruby">Default to Ruby</option>
                  </select>
                  <p className="ad-settings-status" role="status">{settingsStatus}</p>
                </div>
              </div>
            </div>
          </div>

          <h3
            className="mt-2 mb-4 ad-dashboard-section-title"
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {userInfo ? <>Evidence of progress</> : <Skeleton></Skeleton>}
          </h3>
          <div
            className="row no-gutters bg-white rounded-lg shadow mb-4 d-flex align-items-center ad-dashboard-progress"
            style={{ lineHeight: "0" }}
          >
            <div className="col-lg-3 col-12 p-4">
              <small>LESSONS COMPLETED</small>

              <div className="row d-flex align-items-center justify-content-center my-2">
                <div className="col-4">
                  <i className="fa fa-book fa-2x"></i>
                </div>
                <div className="col-8">
                  <h1 className="font-weight-bold">
                    {userInfo && userInfo.completed_lessons ? (
                      userInfo.completed_lessons.length
                    ) : (
                      <Skeleton />
                    )}
                  </h1>
                </div>
              </div>
              <a
                href="/curriculum"
                className="ad-button ad-button-dark w-100 mb-2"
              >
                All courses
              </a>
              <a href="/random-lesson">
                <button className="ad-button ad-button-quiet w-100">
                  Random lesson
                </button>
              </a>
            </div>
            <div className="col-lg-3 col-12 p-4">
              <small>CHALLENGES COMPLETED</small>

              <div className="row d-flex align-items-center justify-content-center my-2">
                <div className="col-4">
                  <i className="fa fa-code fa-2x"></i>
                </div>
                <div className="col-8">
                  <h1 className="font-weight-bold">
                    {userInfo && userInfo.completed_challenges ? (
                      userInfo.completed_challenges.length
                    ) : (
                      <Skeleton />
                    )}
                  </h1>
                </div>
              </div>
              <a
                href="/challenges/daily"
                className="ad-button ad-button-dark w-100 mb-2"
              >
                All problems
              </a>
              <a href="/random-challenge">
                <button className="ad-button ad-button-quiet w-100">
                  Random problem
                </button>
              </a>
            </div>
            <div className="col-lg-6">
              <img src="https://storage.googleapis.com/algodailyrandomassets/personalization/dashboard-mid.svg" />
            </div>
          </div>

          <div className="row ad-dashboard-lower-grid">
            <div className="col-lg-6 mb-4 h-100" id="bottom-left-cell">
              <div
                className="card bg-white border-0 rounded-lg shadow cursor-pointer ad-dashboard-history"
                style={{ height: "100%" }}
              >
                <div className="card-header bg-white">
                  <h3 className="font-weight-bold">Challenge Completions</h3>
                </div>
                <div className="card-body ad-completion-list">
                  {userInfo &&
                  userInfo.completed_challenges &&
                  userInfo.completed_challenges.length ? (
                    <ul className="list-group list-group-flush small">
                      {userInfo.completed_challenges.map((slug) => (
                        <li className="list-group-item pl-0">
                          <Link to={`/challenges/${slug}`}>
                            {titleize(slug.replace(/-/g, " "))}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <>
                      <p>
                        Start practicing some challenges! You can solve them
                        either right in the tutorials, or by going to{" "}
                        <strong>Interactive Mode</strong> on the right hand menu
                        of problems.
                      </p>
                      <a href="/challenges/reverse-a-string" className="ad-button ad-button-primary">Try your first problem</a>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div id="bottom-right-cell" className="col-lg-6 mb-4">
              <div className="card bg-white border-0 rounded-lg shadow ad-dashboard-up-next">
                <div className="card-header bg-white">
                  <h3 className="font-weight-bold">Next Tutorial</h3>
                </div>
                <div className="card-body">
                  <h4>{userInfo.next ? userInfo.next.title : "Your next lesson starts here"}</h4>
                  <p>
                    {userInfo.next ? (
                      extractOnlyText(userInfo.next.excerpt) + "..."
                    ) : (
                      "Begin a course to build your learning history and see what to practice next."
                    )}
                  </p>
                  {userInfo.next ? (
                    <p>
                      <Link
                        to={
                          userInfo.next.question
                            ? `/challenges/${userInfo.next.slug}`
                            : `/lessons/${userInfo.next.slug}`
                        }
                      >
                        <button className="ad-button ad-button-primary">Open next lesson <span aria-hidden="true">→</span></button>
                      </Link>
                    </p>
                  ) : (
                    <a className="ad-button ad-button-primary" href="/curriculum">Explore courses</a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
        <FooterLinks footerLinks={footerLinks} />
      </div>
    </>
  );
};

export default Dashboard;
